import { API_ENDPOINTS } from '../config/api-endpoints.js';
import SessionManager from '../utils/sessionManager.js';

// Check if in production or development
const isProduction = process.env.NODE_ENV === 'production';
export const API_BASE_URL = isProduction
  ? process.env.REACT_APP_BASE_URL
  : (process.env.REACT_APP_BASE_URL || 'https://eft-assured-early.ngrok-free.app/api');

// Debug environment variable loading
console.log('Environment variables:', {
  NODE_ENV: process.env.NODE_ENV,
  isProduction: isProduction,
  REACT_APP_BASE_URL: process.env.REACT_APP_BASE_URL,
  API_BASE_URL: API_BASE_URL
});

// Cache implementation
class APICache {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default
  }

  generateKey(url, options = {}) {
    // Create a unique key based on URL and relevant options
    const method = options.method || 'GET';
    const body = options.body || '';
    return `${method}:${url}:${body}`;
  }
  
  set(key, data, ttl = this.defaultTTL) {
    const expiresAt = Date.now() + ttl;
    this.cache.set(key, {
      data,
      expiresAt
    });
  }
  
  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  clear() {
    this.cache.clear();
  }

  delete(key) {
    this.cache.delete(key);
  }

  // Clear cache entries that match a pattern (useful for invalidating related data)
  clearPattern(pattern) {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }
}

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.cache = new APICache();
    
    // Configure cache TTL for different endpoints (in milliseconds)
    this.cacheTTL = {
      [API_ENDPOINTS.GET_ALL_COURSES]: 10 * 60 * 1000, // 10 minutes for courses
      [API_ENDPOINTS.GET_ALL_USERS]: 15 * 60 * 1000,   // 30 minutes for users
      [API_ENDPOINTS.GET_INSTRUCTORS]: 15 * 60 * 1000, // 15 minutes for instructors
      [API_ENDPOINTS.GET_ALL_LESSONS]: 10 * 60 * 1000, // 10 minutes for lessons
      [API_ENDPOINTS.GET_ALL_QUIZZES]: 10 * 60 * 1000, // 10 minutes for quizzes
      [API_ENDPOINTS.GET_ALL_QUESTIONS]: 10 * 60 * 1000, // 10 minutes for questions
      [API_ENDPOINTS.GET_ALL_ANSWERS]: 5 * 60 * 1000,   // 5 minutes for answers
      [API_ENDPOINTS.GET_ALL_CATEGORIES]: 30 * 60 * 1000, // 30 minutes for categories
      [API_ENDPOINTS.GET_ALL_TOPICS]: 10 * 60 * 1000, // 10 minutes for topics
      [API_ENDPOINTS.GET_ACTIVE_EXAMS]: 5 * 60 * 1000, // 5 minutes for active exams
      [API_ENDPOINTS.GET_ALL_CLASSES]: 10 * 60 * 1000, // 10 minutes for classes
      // Lesson content endpoints - dynamic, so we'll use pattern matching
      'lesson-contents': 5 * 60 * 1000, // 5 minutes for lesson contents
      'topics': 10 * 60 * 1000, // 10 minutes for topics
      'exams': 5 * 60 * 1000, // 5 minutes for exams
      'exam-questions': 5 * 60 * 1000, // 5 minutes for exam questions
      'exam-results': 3 * 60 * 1000, // 3 minutes for exam results
      'exam-availability': 5 * 60 * 1000, // 5 minutes for exam availability
      'classes': 10 * 60 * 1000, // 10 minutes for classes
      'class-students': 5 * 60 * 1000, // 5 minutes for class students
      'attendance': 2 * 60 * 1000, // 2 minutes for attendance
      'exam-types': 15 * 60 * 1000, // 15 minutes for exam types
      'class-feedback': 10 * 60 * 1000, // 10 minutes for class feedback
      'feedback-questions': 10 * 60 * 1000, // 10 minutes for feedback questions
      'feedback-responses': 5 * 60 * 1000, // 5 minutes for feedback responses
      'feedback-data': 5 * 60 * 1000, // 5 minutes for feedback data
      'user-answers': 5 * 60 * 1000, // 5 minutes for user answers
      'user-services': 10 * 60 * 1000, // 10 minutes for user services
      'class-shared-files': 10 * 60 * 1000, // 10 minutes for class shared files
      'class-assignments': 10 * 60 * 1000, // 10 minutes for class assignments
      'assignment-submissions': 5 * 60 * 1000, // 5 minutes for assignment submissions
      default: 5 * 60 * 1000 // 5 minutes default
    };
  }
  
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const cacheKey = this.cache.generateKey(url, options);
    
    // Only cache GET requests
    const isGetRequest = !options.method || options.method.toUpperCase() === 'GET';

    // Check cache first for GET requests
    if (isGetRequest && options.cache !== 'no-store') {
      const cachedData = this.cache.get(cacheKey);
      if (cachedData) {
        console.log('Cache hit for:', endpoint);
        return cachedData;
      }
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken') || localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      console.log('Making API request to:', url);
      console.log('Request config:', config);
      console.log('Request method:', config.method || 'GET');
      
      const response = await fetch(url, config);
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Handle 401 Unauthorized
      if (response.status === 401) {
        // For login requests, throw an error so the Login component can show a message
        const isLoginRequest = url.includes('/admin/login');
        if (isLoginRequest) {
          const error = new Error('Username or password is wrong');
          error.status = 401;
          throw error;
        }

        console.log('Unauthorized access - redirecting to login');
        // Clear all auth tokens and session data
        SessionManager.clearSession();
        localStorage.removeItem('icer_admin_user');
        localStorage.removeItem('admin_token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('refreshToken');
        // Clear cache
        this.cache.clear();
        // Redirect to login page
        window.location.href = '/login';
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data.data);
      
      // Cache GET requests
      if (isGetRequest && options.cache !== 'no-store') {
        let ttl = this.cacheTTL[endpoint];
        
        // Handle dynamic endpoints
        if (!ttl) {
          if (endpoint.includes('lesson-contents')) {
            ttl = this.cacheTTL['lesson-contents'];
          } else if (endpoint.includes('topics')) {
            ttl = this.cacheTTL['topics'];
          } else if (endpoint.includes('exam-questions')) {
            ttl = this.cacheTTL['exam-questions'];
          } else if (endpoint.includes('exam-results')) {
            ttl = this.cacheTTL['exam-results'];
          } else if (endpoint.includes('exam-availability')) {
            ttl = this.cacheTTL['exam-availability'];
          } else if (endpoint.includes('exams')) {
            ttl = this.cacheTTL['exams'];
          } else if (endpoint.includes('class-students')) {
            ttl = this.cacheTTL['class-students'];
          } else if (endpoint.includes('classes')) {
            ttl = this.cacheTTL['classes'];
          } else if (endpoint.includes('attendance')) {
            ttl = this.cacheTTL['attendance'];
          } else if (endpoint.includes('exam-types')) {
            ttl = this.cacheTTL['exam-types'];
          } else if (endpoint.includes('class-feedback') || endpoint.includes('feedback')) {
            if (endpoint.includes('feedback-questions')) {
              ttl = this.cacheTTL['feedback-questions'];
            } else if (endpoint.includes('feedback-responses')) {
              ttl = this.cacheTTL['feedback-responses'];
            } else if (endpoint.includes('feedback-data')) {
              ttl = this.cacheTTL['feedback-data'];
            } else {
              ttl = this.cacheTTL['class-feedback'];
            }
          } else if (endpoint.includes('user-answers')) {
            ttl = this.cacheTTL['user-answers'];
          } else if (endpoint.includes('class-shared-files')) {
            ttl = this.cacheTTL['class-shared-files'];
          } else if (endpoint.includes('assignment-submission')) {
            ttl = this.cacheTTL['assignment-submissions'];
          } else if (endpoint.includes('class-assignment')) {
            ttl = this.cacheTTL['class-assignments'];
          } else {
            ttl = this.cacheTTL.default;
          }
        }
        
        this.cache.set(cacheKey, data, ttl);
        console.log(`Cached response for ${endpoint} with TTL: ${ttl}ms`);
      }
      
      // Invalidate related cache entries for mutations
      if (!isGetRequest) {
        this.invalidateRelatedCache(endpoint);
      }
      
      return data;
    } catch (error) {
      console.error('API request failed:', error.error);
      throw error;
    }
  }

  // Invalidate cache entries when data is modified
  invalidateRelatedCache(endpoint) {
    if (endpoint.includes('user')) {
      this.cache.clearPattern('users');
      this.cache.clearPattern('instructors');
      console.log('Invalidated user-related cache');
    } else if (endpoint.includes('course')) {
      this.cache.clearPattern('courses');
      console.log('Invalidated course-related cache');
    } else if (endpoint.includes('lesson-content')) {
      this.cache.clearPattern('lesson-content');
      console.log('Invalidated lesson-content-related cache');
    } else if (endpoint.includes('lesson')) {
      this.cache.clearPattern('lessons');
      console.log('Invalidated lesson-related cache');
    } else if (endpoint.includes('quiz')) {
      this.cache.clearPattern('quizzes');
      console.log('Invalidated quiz-related cache');
    } else if (endpoint.includes('question')) {
      this.cache.clearPattern('questions');
      console.log('Invalidated question-related cache');
    } else if (endpoint.includes('answer')) {
      this.cache.clearPattern('answers');
      console.log('Invalidated answer-related cache');
    } else if (endpoint.includes('categor')) {
      this.cache.clearPattern('categories');
      console.log('Invalidated category-related cache');
    } else if (endpoint.includes('article')) {
      this.cache.clearPattern('articles');
      console.log('Invalidated article-related cache');
    } else if (endpoint.includes('topic-lesson')) {
      this.cache.clearPattern('topic-lessons');
      console.log('Invalidated topic-lesson-related cache');
    } else if (endpoint.includes('topic')) {
      this.cache.clearPattern('topics');
      console.log('Invalidated topic-related cache');
    } else if (endpoint.includes('exam-question')) {
      this.cache.clearPattern('exam-questions');
      console.log('Invalidated exam-question-related cache');
    } else if (endpoint.includes('exam-result')) {
      this.cache.clearPattern('exam-results');
      console.log('Invalidated exam-result-related cache');
    } else if (endpoint.includes('exam-availability')) {
      this.cache.clearPattern('exam-availability');
      console.log('Invalidated exam-availability-related cache');
    } else if (endpoint.includes('exam')) {
      this.cache.clearPattern('exams');
      console.log('Invalidated exam-related cache');
    } else if (endpoint.includes('class-student')) {
      this.cache.clearPattern('class-students');
      console.log('Invalidated class-student-related cache');
    } else if (endpoint.includes('class')) {
      this.cache.clearPattern('classes');
      console.log('Invalidated class-related cache');
    } else if (endpoint.includes('attendance')) {
      this.cache.clearPattern('attendance');
      console.log('Invalidated attendance-related cache');
    } else if (endpoint.includes('exam-type')) {
      this.cache.clearPattern('exam-types');
      console.log('Invalidated exam-type-related cache');
    } else if (endpoint.includes('feedback')) {
      if (endpoint.includes('feedback-question')) {
        this.cache.clearPattern('feedback-questions');
        console.log('Invalidated feedback-questions-related cache');
      } else if (endpoint.includes('feedback-response')) {
        this.cache.clearPattern('feedback-responses');
        console.log('Invalidated feedback-responses-related cache');
      } else if (endpoint.includes('feedback-data')) {
        this.cache.clearPattern('feedback-data');
        console.log('Invalidated feedback-data-related cache');
      } else {
        this.cache.clearPattern('class-feedback');
        console.log('Invalidated class-feedback-related cache');
      }
    } else if (endpoint.includes('user-answers')) {
      this.cache.clearPattern('user-answers');
      console.log('Invalidated user-answers-related cache');
    } else if (endpoint.includes('user-services')) {
      this.cache.clearPattern('user-services');
      console.log('Invalidated user-services-related cache');
    } else if (endpoint.includes('class-shared-files')) {
      this.cache.clearPattern('class-shared-files');
      console.log('Invalidated class-shared-files-related cache');
    } else if (endpoint.includes('assignment-submission')) {
      this.cache.clearPattern('assignment-submissions');
      console.log('Invalidated assignment-submissions-related cache');
    } else if (endpoint.includes('class-assignment')) {
      this.cache.clearPattern('class-assignments');
      console.log('Invalidated class-assignments-related cache');
    }
  }

  // Method to manually clear cache
  clearCache() {
    this.cache.clear();
    console.log('Cache cleared');
  }

  // Method to clear specific cache pattern
  clearCachePattern(pattern) {
    this.cache.clearPattern(pattern);
    console.log(`Cleared cache for pattern: ${pattern}`);
  }

  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async getWithoutCache(endpoint) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },  
    };

    try {
      console.log('Making uncached API request to:', endpoint);
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Uncached API Response:', data.data);
      
      return data;
    } catch (error) {
      console.error('Uncached API request failed:', error.error);
      throw error;
    }
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

const apiService = new ApiService();

export const authAPI = {
  login: (credentials) => apiService.post(API_ENDPOINTS.LOGIN, credentials),
};

export const adminAPI = {
  createAdmin: (adminData) => apiService.post(API_ENDPOINTS.ADD_ADMIN_USER, adminData),
  loginAdmin: (credentials) => apiService.post(API_ENDPOINTS.LOGIN, credentials),
  logoutAdmin: () => {
    // Clear all stored authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminUser');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('adminUser');
    
    // Clear all API cache
    apiService.clearCache();
    
    return Promise.resolve({ success: true });
  },
  listAdminUsers: () => apiService.get(API_ENDPOINTS.LIST_ADMIN_USERS),
  getAdminUserById: (id) => apiService.get(API_ENDPOINTS.GET_ADMIN_USER_BY_ID(id)),
  updateAdminUser: (adminData) => apiService.put(API_ENDPOINTS.UPDATE_ADMIN_USER(adminData.username), adminData),
  deleteAdminUser : (id) => apiService.delete(API_ENDPOINTS.DELETE_ADMIN_USER(id)),
  refreshToken: (refreshData) => apiService.post(API_ENDPOINTS.REFRESH_TOKEN, refreshData),
};

export const userAPI = {
  createUser: (userData) => apiService.post(API_ENDPOINTS.ADD_USER, userData),
  createUserProfile: (profileData) => apiService.post(API_ENDPOINTS.CREATE_USER_PROFILE, profileData),
  getAllUserProfiles: () => apiService.get(API_ENDPOINTS.GET_ALL_USERS),
  getAllUsers: (options) => apiService.get(API_ENDPOINTS.GET_ALL_USERS, options),
  getUserById: (id) => apiService.get(API_ENDPOINTS.GET_USER_BY_ID(id)),
  updateUser: (userData) => apiService.put(API_ENDPOINTS.UPDATE_USER, userData),
  updatePassword: (passwordData) => apiService.patch(API_ENDPOINTS.UPDATE_PASSWORD, passwordData),
  getUserProfile: (email) => apiService.get(API_ENDPOINTS.GET_USER_PROFILE(email)),
  updateUserProfile: (profileData) => apiService.put(API_ENDPOINTS.UPDATE_USER_PROFILE, profileData),
  getInstructors: () => apiService.get(API_ENDPOINTS.GET_INSTRUCTORS),
  getAllStudents: () => apiService.get(API_ENDPOINTS.GET_ALL_STUDENTS),
  deleteUser: (id) => apiService.delete(API_ENDPOINTS.DELETE_USER(id)),
  // Cache management methods
  clearUserCache: () => apiService.clearCachePattern('users'),
};

export const courseAPI = {
  getAllCourses: () => apiService.get(API_ENDPOINTS.GET_ALL_COURSES),
  createCourse: (courseData) => apiService.post(API_ENDPOINTS.CREATE_COURSE, courseData),
  updateCourse: (courseData) => apiService.put(API_ENDPOINTS.UPDATE_COURSE, courseData),
  deleteCourse: (id) => apiService.delete(API_ENDPOINTS.DELETE_COURSE(id)),
  // Cache management methods
  clearCourseCache: () => apiService.clearCachePattern('courses'),
};

export const lessonAPI = {
  getAllLessons: () => apiService.get(API_ENDPOINTS.GET_ALL_LESSONS),
  createLesson: (lessonData) => apiService.post(API_ENDPOINTS.CREATE_LESSON, lessonData),
  updateLesson: (lessonData) => apiService.put(API_ENDPOINTS.UPDATE_LESSON(lessonData.id), lessonData),
  deleteLesson: (id) => apiService.delete(API_ENDPOINTS.DELETE_LESSON(id)),
  getAllLessonsByCourseId: (courseId) => apiService.get(API_ENDPOINTS.GET_ALL_LESSONS_COURSE(courseId)),
  // Cache management methods
  clearLessonCache: () => apiService.clearCachePattern('lessons'),
}

export const lessonContentAPI = {
  createLessonContent: (contentData) => apiService.post(API_ENDPOINTS.CREATE_LESSON_CONTENT, contentData),
  getLessonContentById: (contentId) => apiService.get(API_ENDPOINTS.GET_LESSON_CONTENT_BY_ID(contentId)),
  getLessonContentsByLessonId: (lessonId) => apiService.get(API_ENDPOINTS.GET_LESSON_CONTENTS_BY_LESSON_ID(lessonId)),
  updateLessonContent: (contentId, contentData) => apiService.put(API_ENDPOINTS.UPDATE_LESSON_CONTENT(contentId), contentData),
  deleteLessonContent: (contentId) => apiService.delete(API_ENDPOINTS.DELETE_LESSON_CONTENT(contentId)),
  deleteAllLessonContentsByLessonId: (lessonId) => apiService.delete(API_ENDPOINTS.DELETE_ALL_LESSON_CONTENTS_BY_LESSON_ID(lessonId)),
  // Cache management methods
  clearLessonContentCache: () => apiService.clearCachePattern('lesson-content'),
};

export const quizAPI = {
  getAllQuizzes: () => apiService.get(API_ENDPOINTS.GET_ALL_QUIZZES),
  createQuiz: (quizData) => apiService.post(API_ENDPOINTS.CREATE_QUIZ, quizData),
  updateQuiz: (quizData) => apiService.put(API_ENDPOINTS.UPDATE_QUIZ, quizData),
  deleteQuiz: (id) => apiService.delete(API_ENDPOINTS.DELETE_QUIZ(id)),
  // Cache management methods
  clearQuizCache: () => apiService.clearCachePattern('quizzes'),
};

export const questionAPI = {
  getAllQuestions: () => apiService.get(API_ENDPOINTS.GET_ALL_QUESTIONS),
  createQuestion: (questionData) => apiService.post(API_ENDPOINTS.CREATE_QUESTION, questionData),
  updateQuestion: (questionData) => apiService.put(API_ENDPOINTS.UPDATE_QUESTION, questionData),
  deleteQuestion: (id) => apiService.delete(API_ENDPOINTS.DELETE_QUESTION(id)),
  getQuestionById: (id) => apiService.get(API_ENDPOINTS.GET_QUESTION_BY_ID(id)),
  getQuestionsByQuizId: (quizId) => apiService.getWithoutCache(API_ENDPOINTS.GET_QUESTIONS_BY_QUIZ_ID(quizId)),
  getQuestionsByLessonId: (lessonId) => apiService.getWithoutCache(API_ENDPOINTS.GET_QUESTIONS_BY_LESSON_ID(lessonId)),
  updateQuestionsQuizId: (data) => apiService.patch(API_ENDPOINTS.UPDATE_QUESTIONS_QUIZ_ID, data),
  // Cache management methods
  clearQuestionCache: () => apiService.clearCachePattern('questions'),
};

export const answerAPI = {
  getAllAnswers: () => apiService.get(API_ENDPOINTS.GET_ALL_ANSWERS),
  createAnswer: (answerData) => apiService.post(API_ENDPOINTS.CREATE_ANSWER, answerData),
  updateAnswer: (answerData) => apiService.put(API_ENDPOINTS.UPDATE_ANSWER, answerData),
  deleteAnswer: (id) => apiService.delete(API_ENDPOINTS.DELETE_ANSWER(id)),
  getAnswersByQuestionId: (questionId) => apiService.get(API_ENDPOINTS.GET_ANSWERS_BY_QUESTION_ID(questionId)),
  // Cache management methods
  clearAnswerCache: () => apiService.clearCachePattern('answers'),
};

export const categoryAPI = {
  getAllCategories: () => apiService.get(API_ENDPOINTS.GET_ALL_CATEGORIES),
  createCategory: (categoryData) => apiService.post(API_ENDPOINTS.CREATE_CATEGORY, categoryData),
  updateCategory: (categoryData) => apiService.put(API_ENDPOINTS.UPDATE_CATEGORY, categoryData),
  deleteCategory: (id) => apiService.delete(API_ENDPOINTS.DELETE_CATEGORY(id)),
  // Cache management methods
  clearCategoryCache: () => apiService.clearCachePattern('categories'),
};

export const articleAPI = {
  getAllArticles: () => apiService.get(API_ENDPOINTS.GET_ALL_ARTICLES),
  getArticleById: (id) => apiService.get(API_ENDPOINTS.GET_ARTICLE_BY_ID(id)),
  createArticle: (articleData) => apiService.post(API_ENDPOINTS.CREATE_ARTICLE, articleData),
  updateArticle: (articleData) => apiService.put(API_ENDPOINTS.UPDATE_ARTICLE, articleData),
  deleteArticle: (id) => apiService.delete(API_ENDPOINTS.DELETE_ARTICLE(id)),
  // Cache management methods
  clearArticleCache: () => apiService.clearCachePattern('articles'),
};

export const topicAPI = {
  getAllTopics: () => apiService.get(API_ENDPOINTS.GET_ALL_TOPICS),
  getTopicById: (id) => apiService.get(API_ENDPOINTS.GET_TOPIC_BY_ID(id)),
  getTopicsByCourseId: (courseId) => apiService.get(API_ENDPOINTS.GET_TOPICS_BY_COURSE_ID(courseId)),
  getTopicsWithLessonsByCourseId: (courseId, classId) => apiService.get(API_ENDPOINTS.GET_TOPICS_LESSONS_BY_COURSE_ID(courseId, classId)),
  createTopic: (topicData) => apiService.post(API_ENDPOINTS.CREATE_TOPIC, topicData),
  updateTopic: (id, topicData) => apiService.put(API_ENDPOINTS.UPDATE_TOPIC(id), topicData),
  deleteTopic: (id) => apiService.delete(API_ENDPOINTS.DELETE_TOPIC(id)),
  // Cache management methods
  clearTopicCache: () => apiService.clearCachePattern('topics'),
};

export const topicLessonAPI = {
  createTopicLesson: (topicLessonData) => apiService.post(API_ENDPOINTS.CREATE_TOPIC_LESSON, topicLessonData),
  getTopicLessonsByTopicId: (topicId) => apiService.get(API_ENDPOINTS.GET_TOPIC_LESSONS_BY_TOPIC_ID(topicId)),
  deleteTopicLesson: (id) => apiService.delete(API_ENDPOINTS.DELETE_TOPIC_LESSON(id)),
  updateTopicLesson: (id, topicLessonData) => apiService.put(API_ENDPOINTS.UPDATE_TOPIC_LESSON(id), topicLessonData),
  // Cache management methods
  clearTopicLessonCache: () => apiService.clearCachePattern('topic-lessons'),
};

export const examAPI = {
  getAllExams: () => apiService.get(API_ENDPOINTS.GET_ALL_EXAMS),
  getExamById: (id) => apiService.get(API_ENDPOINTS.GET_EXAM_BY_ID(id)),
  getExamsByCourseId: (courseId) => apiService.get(API_ENDPOINTS.GET_EXAMS_BY_COURSE_ID(courseId)),
  getActiveExams: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_EXAMS),
  createExam: (examData) => apiService.post(API_ENDPOINTS.CREATE_EXAM, examData),
  updateExam: (id, examData) => apiService.put(API_ENDPOINTS.UPDATE_EXAM(id), examData),
  deleteExam: (id) => apiService.delete(API_ENDPOINTS.DELETE_EXAM(id)),
  activateExam: (id) => apiService.put(API_ENDPOINTS.ACTIVATE_EXAM(id)),
  deactivateExam: (id) => apiService.put(API_ENDPOINTS.DEACTIVATE_EXAM(id)),
  // Cache management methods
  clearExamCache: () => apiService.clearCachePattern('exams'),
};

export const examQuestionAPI = {
  createExamQuestion: (examQuestionData) => apiService.post(API_ENDPOINTS.CREATE_EXAM_QUESTION, examQuestionData),
  getExamQuestionsByExamId: (examId) => apiService.get(API_ENDPOINTS.GET_EXAM_QUESTIONS_BY_EXAM_ID(examId)),
  deleteExamQuestion: (id) => apiService.delete(API_ENDPOINTS.DELETE_EXAM_QUESTION(id)),
  // Cache management methods
  clearExamQuestionCache: () => apiService.clearCachePattern('exam-questions'),
};

export const examResultAPI = {
  createExamResult: (examResultData) => apiService.post(API_ENDPOINTS.CREATE_EXAM_RESULT, examResultData),
  getExamResultsByUserId: (userId) => apiService.get(API_ENDPOINTS.GET_EXAM_RESULTS_BY_USER_ID(userId)),
  getExamResultsByExamId: (examId) => apiService.get(API_ENDPOINTS.GET_EXAM_RESULTS_BY_EXAM_ID(examId)),
  // Cache management methods
  clearExamResultCache: () => apiService.clearCachePattern('exam-results'),
};

export const examAvailabilityAPI = {
  createExamAvailability: (examAvailabilityData) => apiService.post(API_ENDPOINTS.CREATE_EXAM_AVAILABILITY, examAvailabilityData),
  getExamAvailabilityByExamId: (examId, options) => apiService.get(API_ENDPOINTS.GET_EXAM_AVAILABILITY_BY_EXAM_ID(examId), options),
  deleteExamAvailability: (id) => apiService.delete(API_ENDPOINTS.DELETE_EXAM_AVAILABILITY(id)),
  // Cache management methods
  clearExamAvailabilityCache: () => apiService.clearCachePattern('exam-availability'),
};

export const classAPI = {
  getAllClasses: (options) => apiService.get(API_ENDPOINTS.GET_ALL_CLASSES, options),
  getClassById: (id) => apiService.get(API_ENDPOINTS.GET_CLASS_BY_ID(id)),
  getClassByCode: (classCode) => apiService.get(API_ENDPOINTS.GET_CLASS_BY_CODE(classCode)),
  getClassesByCourseId: (courseId) => apiService.get(API_ENDPOINTS.GET_CLASSES_BY_COURSE_ID(courseId)),
  getClassesByInstructorId: (instructorId) => apiService.get(API_ENDPOINTS.GET_CLASSES_BY_INSTRUCTOR_ID(instructorId)),
  getClassDashboard: (classId) => apiService.get(API_ENDPOINTS.GET_CLASS_DASHBOARD(classId)),
  createClass: (classData) => apiService.post(API_ENDPOINTS.CREATE_CLASS, classData),
  updateClass: (id, classData) => apiService.put(API_ENDPOINTS.UPDATE_CLASS(id), classData),
  deleteClass: (id) => apiService.delete(API_ENDPOINTS.DELETE_CLASS(id)),
  checkInstructorForLesson: (data) => apiService.post(API_ENDPOINTS.CLASS_LESSON_CHECK_INSTRUCTOR, data),
  deleteInstructorForLesson : (lessonId, classId) => apiService.delete(API_ENDPOINTS.CLASS_LESSON_DELETE_INSTRUCTOR(lessonId, classId)),
  // Cache management methods
  clearClassCache: () => apiService.clearCachePattern('classes'),
};

export const classStudentAPI = {
  enrollStudent: (enrollmentData) => apiService.post(API_ENDPOINTS.ENROLL_STUDENT, enrollmentData),
  getStudentsInClass: (classId) => apiService.get(API_ENDPOINTS.GET_STUDENTS_IN_CLASS(classId)),
  getClassesForStudent: (studentId) => apiService.get(API_ENDPOINTS.GET_CLASSES_FOR_STUDENT(studentId)),
  deleteStudentFromClass: (classId, studentId) => apiService.delete(API_ENDPOINTS.DELETE_STUDENT_FROM_CLASS(classId, studentId)),
  // Cache management methods
  clearClassStudentCache: () => apiService.clearCachePattern('class-students'),
};

export const attendanceAPI = {
  markAttendance: (attendanceData) => apiService.post(API_ENDPOINTS.MARK_ATTENDANCE, attendanceData),
  getAttendanceForClass: (classId) => apiService.get(API_ENDPOINTS.GET_ATTENDANCE_FOR_CLASS(classId)),
  getStudentAttendance: (classId, studentId) => apiService.get(API_ENDPOINTS.GET_STUDENT_ATTENDANCE(classId, studentId)),
  // Cache management methods
  clearAttendanceCache: () => apiService.clearCachePattern('attendance'),
};

export const losAPI = {
  createLOS: (losData) => apiService.post(API_ENDPOINTS.CREATE_LOS, losData),
  getLOSById: (id) => apiService.get(API_ENDPOINTS.GET_LOS_BY_ID(id)),
  getLOSByLessonId: (lessonId) => apiService.get(API_ENDPOINTS.GET_LOS_BY_LESSON_ID(lessonId)),
  getAllLOS: () => apiService.get(API_ENDPOINTS.GET_ALL_LOS),
  updateLOS: (id, losData) => apiService.put(API_ENDPOINTS.UPDATE_LOS(id), losData),
  deleteLOS: (id) => apiService.delete(API_ENDPOINTS.DELETE_LOS(id)),
  deleteLOSByLessonId: (lessonId) => apiService.delete(API_ENDPOINTS.DELETE_LOS_BY_LESSON_ID(lessonId)),
  searchLOS: (params) => apiService.get(API_ENDPOINTS.SEARCH_LOS, { params }),
  // Cache management methods
  clearLOSCache: () => apiService.clearCachePattern('los'),
};



export const examTypeAPI = {
  createExamType: (examTypeData) => apiService.post(API_ENDPOINTS.CREATE_EXAM_TYPE, examTypeData),
  getExamTypeById: (id) => apiService.get(API_ENDPOINTS.GET_EXAM_TYPE_BY_ID(id)),
  getExamTypeByNameAndCourse: (courseId, name) => apiService.get(API_ENDPOINTS.GET_EXAM_TYPE_BY_NAME_AND_COURSE(courseId, name)),
  getAllExamTypes: () => apiService.get(API_ENDPOINTS.GET_ALL_EXAM_TYPES),
  getExamTypesByCourseId: (courseId) => apiService.get(API_ENDPOINTS.GET_EXAM_TYPES_BY_COURSE_ID(courseId)),
  updateExamType: (id, examTypeData) => apiService.put(API_ENDPOINTS.UPDATE_EXAM_TYPE(id), examTypeData),
  deleteExamType: (id) => apiService.delete(API_ENDPOINTS.DELETE_EXAM_TYPE(id)),
  deleteExamTypesByCourseId: (courseId) => apiService.delete(API_ENDPOINTS.DELETE_EXAM_TYPES_BY_COURSE_ID(courseId)),
  searchExamTypes: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`${API_ENDPOINTS.SEARCH_EXAM_TYPES}?${queryString}`);
  },
  searchExamTypesByCourse: (courseId, params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`${API_ENDPOINTS.SEARCH_EXAM_TYPES_BY_COURSE(courseId)}?${queryString}`);
  },
  // Cache management methods
  clearExamTypeCache: () => apiService.clearCachePattern('exam-types'),
};

export const classFeedbackAPI = {
  // Class Feedback CRUD operations
  createClassFeedback: (feedbackData) => apiService.post(API_ENDPOINTS.CREATE_CLASS_FEEDBACK, feedbackData),
  getClassFeedbackById: (id) => apiService.get(API_ENDPOINTS.GET_CLASS_FEEDBACK_BY_ID(id)),
  getClassFeedbacksByClassId: (classId) => apiService.get(API_ENDPOINTS.GET_CLASS_FEEDBACKS_BY_CLASS_ID(classId)),
  getActiveClassFeedbacks: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_CLASS_FEEDBACKS),
  updateClassFeedback: (id, feedbackData) => apiService.put(API_ENDPOINTS.UPDATE_CLASS_FEEDBACK(id), feedbackData),
  activateClassFeedback: (id) => apiService.put(API_ENDPOINTS.ACTIVATE_CLASS_FEEDBACK(id)),
  deleteClassFeedback: (id) => apiService.delete(API_ENDPOINTS.DELETE_CLASS_FEEDBACK(id)),
  // Cache management methods
  clearClassFeedbackCache: () => apiService.clearCachePattern('class-feedback'),
};

export const feedbackQuestionAPI = {
  // Feedback Questions CRUD operations
  createFeedbackQuestion: (questionData) => apiService.post(API_ENDPOINTS.CREATE_FEEDBACK_QUESTION, questionData),
  getFeedbackQuestionsByFeedbackId: (feedbackId) => apiService.get(API_ENDPOINTS.GET_FEEDBACK_QUESTIONS_BY_FEEDBACK_ID(feedbackId)),
  updateFeedbackQuestion: (id, questionData) => apiService.put(API_ENDPOINTS.UPDATE_FEEDBACK_QUESTION(id), questionData),
  deleteFeedbackQuestion: (id) => apiService.delete(API_ENDPOINTS.DELETE_FEEDBACK_QUESTION(id)),
  // Cache management methods
  clearFeedbackQuestionCache: () => apiService.clearCachePattern('feedback-questions'),
};

export const feedbackResponseAPI = {
  // Feedback Responses CRUD operations
  createFeedbackResponse: (responseData) => apiService.post(API_ENDPOINTS.CREATE_FEEDBACK_RESPONSE, responseData),
  getFeedbackResponsesByFeedbackId: (feedbackId) => apiService.get(API_ENDPOINTS.GET_FEEDBACK_RESPONSES_BY_FEEDBACK_ID(feedbackId)),
  getFeedbackResponsesByUserId: (userId) => apiService.get(API_ENDPOINTS.GET_FEEDBACK_RESPONSES_BY_USER_ID(userId)),
  updateFeedbackResponse: (id, responseData) => apiService.put(API_ENDPOINTS.UPDATE_FEEDBACK_RESPONSE(id), responseData),
  deleteFeedbackResponse: (id) => apiService.delete(API_ENDPOINTS.DELETE_FEEDBACK_RESPONSE(id)),
  // Cache management methods
  clearFeedbackResponseCache: () => apiService.clearCachePattern('feedback-responses'),
};

export const feedbackDataAPI = {
  // Combined Feedback Data operations
  getCompleteFeedbackData: (feedbackId) => apiService.get(API_ENDPOINTS.GET_COMPLETE_FEEDBACK_DATA(feedbackId)),
  getUserCompleteFeedbackData: (userId) => apiService.get(API_ENDPOINTS.GET_USER_COMPLETE_FEEDBACK_DATA(userId)),
  // Cache management methods
  clearFeedbackDataCache: () => apiService.clearCachePattern('feedback-data'),
};

export const userAnswerAPI = {
  // User Answers operations
  getUserAnswersByClassId: (classId) => apiService.get(API_ENDPOINTS.GET_USER_ANSWERS_BY_CLASS_ID(classId)),
  getIndividualDashboard: (firstName, lastName) => apiService.get(API_ENDPOINTS.GET_INDIVIDUAL_DASHBOARD(firstName, lastName)),
  // Cache management methods
  clearUserAnswerCache: () => apiService.clearCachePattern('user-answers'),
};

export const userServiceAPI = {
  // User Service CRUD operations
  createUserService: (userServiceData) => apiService.post(API_ENDPOINTS.CREATE_USER_SERVICE, userServiceData),
  getUserServiceById: (id) => apiService.get(API_ENDPOINTS.GET_USER_SERVICE_BY_ID(id)),
  getUserServicesByUserId: (userId) => apiService.get(API_ENDPOINTS.GET_USER_SERVICES_BY_USER_ID(userId)),
  getActiveUserServicesByUserId: (userId) => apiService.get(API_ENDPOINTS.GET_ACTIVE_USER_SERVICES_BY_USER_ID(userId)),
  getUserServiceByUserAndCourse: (userId, courseId) => apiService.get(API_ENDPOINTS.GET_USER_SERVICE_BY_USER_AND_COURSE(userId, courseId)),
  updateUserService: (id, userServiceData) => apiService.put(API_ENDPOINTS.UPDATE_USER_SERVICE(id), userServiceData),
  extendUserService: (id, extensionData) => apiService.put(API_ENDPOINTS.EXTEND_USER_SERVICE(id), extensionData),
  deleteUserService: (id) => apiService.delete(API_ENDPOINTS.DELETE_USER_SERVICE(id)),
  deleteUserServicesByUserId: (userId) => apiService.delete(API_ENDPOINTS.DELETE_USER_SERVICES_BY_USER_ID(userId)),
  // Cache management methods
  clearUserServiceCache: () => apiService.clearCachePattern('user-services'),
};

export const classSharedFileAPI = {
  // Class Shared Files CRUD operations
  createClassSharedFile: (fileData) => apiService.post(API_ENDPOINTS.CREATE_CLASS_SHARED_FILE, fileData),
  getAllClassSharedFiles: () => apiService.get(API_ENDPOINTS.GET_ALL_CLASS_SHARED_FILES),
  getClassSharedFileById: (id) => apiService.get(API_ENDPOINTS.GET_CLASS_SHARED_FILE_BY_ID(id)),
  getClassSharedFilesByClassId: (classId) => apiService.get(API_ENDPOINTS.GET_CLASS_SHARED_FILES_BY_CLASS_ID(classId)),
  getClassSharedFilesByUploader: (uploaderId) => apiService.get(API_ENDPOINTS.GET_CLASS_SHARED_FILES_BY_UPLOADER(uploaderId)),
  getClassSharedFilesByType: (fileType) => apiService.get(API_ENDPOINTS.GET_CLASS_SHARED_FILES_BY_TYPE(fileType)),
  updateClassSharedFile: (id, fileData) => apiService.put(API_ENDPOINTS.UPDATE_CLASS_SHARED_FILE(id), fileData),
  deleteClassSharedFile: (id) => apiService.delete(API_ENDPOINTS.DELETE_CLASS_SHARED_FILE(id)),
  deleteClassSharedFilesByClass: (classId) => apiService.delete(API_ENDPOINTS.DELETE_CLASS_SHARED_FILES_BY_CLASS(classId)),
  
  // Search and filter operations
  searchClassSharedFiles: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`${API_ENDPOINTS.SEARCH_CLASS_SHARED_FILES}?${queryString}`);
  },
  
  // Statistics and analytics operations
  countAllClassSharedFiles: () => apiService.get(API_ENDPOINTS.COUNT_ALL_CLASS_SHARED_FILES),
  countClassSharedFilesByClass: (classId) => apiService.get(API_ENDPOINTS.COUNT_CLASS_SHARED_FILES_BY_CLASS(classId)),
  getRecentClassSharedFiles: () => apiService.get(API_ENDPOINTS.GET_RECENT_CLASS_SHARED_FILES),
  
  // User details join operations
  getAllClassSharedFilesWithUserDetails: () => apiService.get(API_ENDPOINTS.GET_CLASS_SHARED_FILES_WITH_USER_DETAILS),
  getClassSharedFileWithUserDetails: (id) => apiService.get(API_ENDPOINTS.GET_CLASS_SHARED_FILE_WITH_USER_DETAILS(id)),
  
  // Cache management methods
  clearClassSharedFileCache: () => apiService.clearCachePattern('class-shared-files'),
};

export const classAssignmentAPI = {
  // Class Assignments CRUD operations
  createClassAssignment: (assignmentData) => apiService.post(API_ENDPOINTS.CREATE_CLASS_ASSIGNMENT, assignmentData),
  getAllClassAssignments: () => apiService.get(API_ENDPOINTS.GET_ALL_CLASS_ASSIGNMENTS),
  getClassAssignmentById: (id) => apiService.get(API_ENDPOINTS.GET_CLASS_ASSIGNMENT_BY_ID(id)),
  getClassAssignmentsByClassId: (classId) => apiService.get(API_ENDPOINTS.GET_CLASS_ASSIGNMENTS_BY_CLASS_ID(classId)),
  getClassAssignmentsByCreator: (creatorId) => apiService.get(API_ENDPOINTS.GET_CLASS_ASSIGNMENTS_BY_CREATOR(creatorId)),
  getClassAssignmentsByType: (assignmentType) => apiService.get(API_ENDPOINTS.GET_CLASS_ASSIGNMENTS_BY_TYPE(assignmentType)),
  getUpcomingClassAssignments: () => apiService.get(API_ENDPOINTS.GET_UPCOMING_CLASS_ASSIGNMENTS),
  getOverdueClassAssignments: () => apiService.get(API_ENDPOINTS.GET_OVERDUE_CLASS_ASSIGNMENTS),
  updateClassAssignment: (id, assignmentData) => apiService.put(API_ENDPOINTS.UPDATE_CLASS_ASSIGNMENT(id), assignmentData),
  deleteClassAssignment: (id) => apiService.delete(API_ENDPOINTS.DELETE_CLASS_ASSIGNMENT(id)),
  deleteClassAssignmentsByClass: (classId) => apiService.delete(API_ENDPOINTS.DELETE_CLASS_ASSIGNMENTS_BY_CLASS(classId)),
  
  // Search and filter operations
  searchClassAssignments: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`${API_ENDPOINTS.SEARCH_CLASS_ASSIGNMENTS}?${queryString}`);
  },
  
  // Statistics operations
  countAllClassAssignments: () => apiService.get(API_ENDPOINTS.COUNT_ALL_CLASS_ASSIGNMENTS),
  countClassAssignmentsByClass: (classId) => apiService.get(API_ENDPOINTS.COUNT_CLASS_ASSIGNMENTS_BY_CLASS(classId)),
  
  // Creator details join operations
  getAllClassAssignmentsWithCreatorDetails: () => apiService.get(API_ENDPOINTS.GET_CLASS_ASSIGNMENTS_WITH_CREATOR_DETAILS),
  getClassAssignmentWithCreatorDetails: (id) => apiService.get(API_ENDPOINTS.GET_CLASS_ASSIGNMENT_WITH_CREATOR_DETAILS(id)),
  
  // Cache management methods
  clearClassAssignmentCache: () => apiService.clearCachePattern('class-assignments'),
};

export const assignmentSubmissionAPI = {
  // Assignment Submissions CRUD operations
  createAssignmentSubmission: (submissionData) => apiService.post(API_ENDPOINTS.CREATE_ASSIGNMENT_SUBMISSION, submissionData),
  getAllAssignmentSubmissions: () => apiService.get(API_ENDPOINTS.GET_ALL_ASSIGNMENT_SUBMISSIONS),
  getAssignmentSubmissionById: (id) => apiService.get(API_ENDPOINTS.GET_ASSIGNMENT_SUBMISSION_BY_ID(id)),
  getAssignmentSubmissionsByAssignment: (assignmentId) => apiService.get(API_ENDPOINTS.GET_ASSIGNMENT_SUBMISSIONS_BY_ASSIGNMENT(assignmentId)),
  getAssignmentSubmissionsByUser: (userId) => apiService.get(API_ENDPOINTS.GET_ASSIGNMENT_SUBMISSIONS_BY_USER(userId)),
  getAssignmentSubmissionByAssignmentAndUser: (assignmentId, userId) => apiService.get(API_ENDPOINTS.GET_ASSIGNMENT_SUBMISSION_BY_ASSIGNMENT_AND_USER(assignmentId, userId)),
  getAssignmentSubmissionsByStatus: (status) => apiService.get(API_ENDPOINTS.GET_ASSIGNMENT_SUBMISSIONS_BY_STATUS(status)),
  getPendingAssignmentSubmissions: () => apiService.get(API_ENDPOINTS.GET_PENDING_ASSIGNMENT_SUBMISSIONS),
  getGradedAssignmentSubmissions: () => apiService.get(API_ENDPOINTS.GET_GRADED_ASSIGNMENT_SUBMISSIONS),
  updateAssignmentSubmission: (id, submissionData) => apiService.put(API_ENDPOINTS.UPDATE_ASSIGNMENT_SUBMISSION(id), submissionData),
  gradeAssignmentSubmission: (id, gradeData) => apiService.post(API_ENDPOINTS.GRADE_ASSIGNMENT_SUBMISSION(id), gradeData),
  deleteAssignmentSubmission: (id) => apiService.delete(API_ENDPOINTS.DELETE_ASSIGNMENT_SUBMISSION(id)),
  deleteAssignmentSubmissionsByAssignment: (assignmentId) => apiService.delete(API_ENDPOINTS.DELETE_ASSIGNMENT_SUBMISSIONS_BY_ASSIGNMENT(assignmentId)),
  
  // Statistics operations
  countAllAssignmentSubmissions: () => apiService.get(API_ENDPOINTS.COUNT_ALL_ASSIGNMENT_SUBMISSIONS),
  countAssignmentSubmissionsByAssignment: (assignmentId) => apiService.get(API_ENDPOINTS.COUNT_ASSIGNMENT_SUBMISSIONS_BY_ASSIGNMENT(assignmentId)),
  countAssignmentSubmissionsByStatus: (status) => apiService.get(API_ENDPOINTS.COUNT_ASSIGNMENT_SUBMISSIONS_BY_STATUS(status)),
  
  // User details join operations
  getAllAssignmentSubmissionsWithUserDetails: () => apiService.get(API_ENDPOINTS.GET_ASSIGNMENT_SUBMISSIONS_WITH_USER_DETAILS),
  getAssignmentSubmissionWithDetails: (id) => apiService.get(API_ENDPOINTS.GET_ASSIGNMENT_SUBMISSION_WITH_DETAILS(id)),
  getAssignmentSubmissionsWithUserDetailsByAssignment: (assignmentId) => apiService.get(API_ENDPOINTS.GET_ASSIGNMENT_SUBMISSIONS_WITH_USER_DETAILS_BY_ASSIGNMENT(assignmentId)),
  
  // Cache management methods
  clearAssignmentSubmissionCache: () => apiService.clearCachePattern('assignment-submissions'),
};

// WEBDATA API - Hero Section
export const heroAPI = {
  getActiveHero: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_HERO),
  getAllHeroes: () => apiService.get(API_ENDPOINTS.GET_ALL_HEROES),
  createHero: (data) => apiService.post(API_ENDPOINTS.CREATE_HERO, data),
  updateHero: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_HERO(id), data),
  activateHero: (id) => apiService.put(API_ENDPOINTS.ACTIVATE_HERO(id)),
  deleteHero: (id) => apiService.delete(API_ENDPOINTS.DELETE_HERO(id)),
};

// WEBDATA API - Feature Cards
export const featureCardAPI = {
  getActiveFeatureCards: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_FEATURE_CARDS),
  getAllFeatureCards: () => apiService.get(API_ENDPOINTS.GET_ALL_FEATURE_CARDS),
  getFeatureCardById: (id) => apiService.get(API_ENDPOINTS.GET_FEATURE_CARD_BY_ID(id)),
  createFeatureCard: (data) => apiService.post(API_ENDPOINTS.CREATE_FEATURE_CARD, data),
  updateFeatureCard: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_FEATURE_CARD(id), data),
  toggleFeatureCard: (id, data) => apiService.put(API_ENDPOINTS.TOGGLE_FEATURE_CARD(id), data),
  deleteFeatureCard: (id) => apiService.delete(API_ENDPOINTS.DELETE_FEATURE_CARD(id)),
};

// WEBDATA API - Important Dates
export const importantDateAPI = {
  getActiveImportantDates: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_IMPORTANT_DATES),
  getAllImportantDates: () => apiService.get(API_ENDPOINTS.GET_ALL_IMPORTANT_DATES),
  getImportantDateById: (id) => apiService.get(API_ENDPOINTS.GET_IMPORTANT_DATE_BY_ID(id)),
  createImportantDate: (data) => apiService.post(API_ENDPOINTS.CREATE_IMPORTANT_DATE, data),
  updateImportantDate: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_IMPORTANT_DATE(id), data),
  toggleImportantDate: (id, data) => apiService.put(API_ENDPOINTS.TOGGLE_IMPORTANT_DATE(id), data),
  deleteImportantDate: (id) => apiService.delete(API_ENDPOINTS.DELETE_IMPORTANT_DATE(id)),
};

// WEBDATA API - Statistics
export const statisticAPI = {
  getActiveStatistics: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_STATISTICS),
  getAllStatistics: () => apiService.get(API_ENDPOINTS.GET_ALL_STATISTICS),
  getStatisticById: (id) => apiService.get(API_ENDPOINTS.GET_STATISTIC_BY_ID(id)),
  createStatistic: (data) => apiService.post(API_ENDPOINTS.CREATE_STATISTIC, data),
  updateStatistic: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_STATISTIC(id), data),
  toggleStatistic: (id, data) => apiService.put(API_ENDPOINTS.TOGGLE_STATISTIC(id), data),
  deleteStatistic: (id) => apiService.delete(API_ENDPOINTS.DELETE_STATISTIC(id)),
};

// WEBDATA API - Teachers
export const teacherAPI = {
  getActiveTeachers: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_TEACHERS),
  getAllTeachers: () => apiService.get(API_ENDPOINTS.GET_ALL_TEACHERS),
  getTeacherById: (id) => apiService.get(API_ENDPOINTS.GET_TEACHER_BY_ID(id)),
  createTeacher: (data) => apiService.post(API_ENDPOINTS.CREATE_TEACHER, data),
  updateTeacher: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_TEACHER(id), data),
  toggleTeacher: (id, data) => apiService.put(API_ENDPOINTS.TOGGLE_TEACHER(id), data),
  deleteTeacher: (id) => apiService.delete(API_ENDPOINTS.DELETE_TEACHER(id)),
};

// WEBDATA API - Collaborating Companies
export const companyAPI = {
  getActiveCompanies: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_COMPANIES),
  getAllCompanies: () => apiService.get(API_ENDPOINTS.GET_ALL_COMPANIES),
  getCompanyById: (id) => apiService.get(API_ENDPOINTS.GET_COMPANY_BY_ID(id)),
  createCompany: (data) => apiService.post(API_ENDPOINTS.CREATE_COMPANY, data),
  updateCompany: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_COMPANY(id), data),
  toggleCompany: (id, data) => apiService.put(API_ENDPOINTS.TOGGLE_COMPANY(id), data),
  deleteCompany: (id) => apiService.delete(API_ENDPOINTS.DELETE_COMPANY(id)),
};

// WEBDATA API - Web Courses
export const webCourseAPI = {
  getActiveWebCourses: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_WEB_COURSES),
  getAllWebCourses: () => apiService.get(API_ENDPOINTS.GET_ALL_WEB_COURSES, { cache: 'no-store' }),
  getWebCourseById: (id) => apiService.get(API_ENDPOINTS.GET_WEB_COURSE_BY_ID(id)),
  createWebCourse: (data) => apiService.post(API_ENDPOINTS.CREATE_WEB_COURSE, data),
  updateWebCourse: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_WEB_COURSE(id), data),
  toggleWebCourse: (id, data) => apiService.put(API_ENDPOINTS.TOGGLE_WEB_COURSE(id), data),
  deleteWebCourse: (id) => apiService.delete(API_ENDPOINTS.DELETE_WEB_COURSE(id)),
};

// WEBDATA API - Web Course Registration Forms
export const courseRegistrationFormAPI = {
  getActiveCourseRegistrationForms: () => apiService.get(API_ENDPOINTS.GET_ACTIVE_COURSE_REGISTRATION_FORMS),
  getAllCourseRegistrationForms: () => apiService.get(API_ENDPOINTS.GET_ALL_COURSE_REGISTRATION_FORMS),
  getCourseRegistrationFormById: (id) => apiService.get(API_ENDPOINTS.GET_COURSE_REGISTRATION_FORM_BY_ID(id)),
  createCourseRegistrationForm: (data) => apiService.post(API_ENDPOINTS.CREATE_COURSE_REGISTRATION_FORM, data),
  updateCourseRegistrationForm: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_COURSE_REGISTRATION_FORM(id), data),
  toggleCourseRegistrationForm: (id, data) => apiService.put(API_ENDPOINTS.TOGGLE_COURSE_REGISTRATION_FORM(id), data),
  deleteCourseRegistrationForm: (id) => apiService.delete(API_ENDPOINTS.DELETE_COURSE_REGISTRATION_FORM(id)),
};

// WEBDATA API - Form Questions
export const formQuestionAPI = {
  getAllFormQuestions: () => apiService.get(API_ENDPOINTS.GET_ALL_FORM_QUESTIONS),
  getFormQuestionById: (id) => apiService.get(API_ENDPOINTS.GET_FORM_QUESTION_BY_ID(id)),
  getFormQuestionsByFormId: (formId) => apiService.get(API_ENDPOINTS.GET_FORM_QUESTIONS_BY_FORM_ID(formId)),
  createFormQuestion: (data) => apiService.post(API_ENDPOINTS.CREATE_FORM_QUESTION, data),
  updateFormQuestion: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_FORM_QUESTION(id), data),
  deleteFormQuestion: (id) => apiService.delete(API_ENDPOINTS.DELETE_FORM_QUESTION(id)),
  deleteFormQuestionsByFormId: (formId) => apiService.delete(API_ENDPOINTS.DELETE_FORM_QUESTIONS_BY_FORM_ID(formId)),
};

// WEBDATA API - Form Results (Submissions)
export const formResultAPI = {
  getAllFormResults: () => apiService.get(API_ENDPOINTS.GET_ALL_FORM_RESULTS),
  getFormResultById: (id) => apiService.get(API_ENDPOINTS.GET_FORM_RESULT_BY_ID(id)),
  getFormResultsByFormId: (formId) => apiService.get(API_ENDPOINTS.GET_FORM_RESULTS_BY_FORM_ID(formId)),
  getFormResultsByUserEmail: (email) => apiService.get(API_ENDPOINTS.GET_FORM_RESULTS_BY_USER_EMAIL(email)),
  createFormResult: (data) => apiService.post(API_ENDPOINTS.CREATE_FORM_RESULT, data),
  updateFormResult: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_FORM_RESULT(id), data),
  deleteFormResult: (id) => apiService.delete(API_ENDPOINTS.DELETE_FORM_RESULT(id)),
  deleteFormResultsByFormId: (formId) => apiService.delete(API_ENDPOINTS.DELETE_FORM_RESULTS_BY_FORM_ID(formId)),
};

// WEBDATA API - Web Contact Us
export const contactUsAPI = {
  getAllContactMessages: () => apiService.get(API_ENDPOINTS.GET_ALL_CONTACT_MESSAGES),
  getContactMessageById: (id) => apiService.get(API_ENDPOINTS.GET_CONTACT_MESSAGE_BY_ID(id)),
  getContactMessagesByEmail: (email) => apiService.get(API_ENDPOINTS.GET_CONTACT_MESSAGES_BY_EMAIL(email)),
  searchContactMessages: (query) => apiService.get(API_ENDPOINTS.SEARCH_CONTACT_MESSAGES(query)),
  createContactMessage: (data) => apiService.post(API_ENDPOINTS.CREATE_CONTACT_MESSAGE, data),
  updateContactMessage: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_CONTACT_MESSAGE(id), data),
  deleteContactMessage: (id) => apiService.delete(API_ENDPOINTS.DELETE_CONTACT_MESSAGE(id)),
};

// WEBDATA API - Web Course FAQs
export const webCourseFaqAPI = {
  getAllWebCourseFaqs: () => apiService.get(API_ENDPOINTS.GET_ALL_WEB_COURSE_FAQS),
  getWebCourseFaqById: (id) => apiService.get(API_ENDPOINTS.GET_WEB_COURSE_FAQ_BY_ID(id)),
  getWebCourseFaqsByCourseId: (webCourseId) => apiService.get(API_ENDPOINTS.GET_WEB_COURSE_FAQS_BY_COURSE_ID(webCourseId)),
  createWebCourseFaq: (data) => apiService.post(API_ENDPOINTS.CREATE_WEB_COURSE_FAQ, data),
  updateWebCourseFaq: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_WEB_COURSE_FAQ(id), data),
  deleteWebCourseFaq: (id) => apiService.delete(API_ENDPOINTS.DELETE_WEB_COURSE_FAQ(id)),
};

// Exam User Retake API
export const examUserRetakeAPI = {
  createExamUserRetake: (data) => apiService.post(API_ENDPOINTS.CREATE_EXAM_USER_RETAKE, data),
  getExamUserRetakeById: (id) => apiService.get(API_ENDPOINTS.GET_EXAM_USER_RETAKE_BY_ID(id)),
  getExamUserRetakeByExamAndUser: (examId, userId) => apiService.get(API_ENDPOINTS.GET_EXAM_USER_RETAKE_BY_EXAM_AND_USER(examId, userId)),
  getAllExamUserRetakes: () => apiService.get(API_ENDPOINTS.GET_ALL_EXAM_USER_RETAKES),
  getExamUserRetakesByExamId: (examId) => apiService.get(API_ENDPOINTS.GET_EXAM_USER_RETAKES_BY_EXAM_ID(examId)),
  getExamUserRetakesByUserId: (userId) => apiService.get(API_ENDPOINTS.GET_EXAM_USER_RETAKES_BY_USER_ID(userId)),
  getRetakesByExamId: (examId) => apiService.get(API_ENDPOINTS.GET_RETAKES_BY_EXAM_ID(examId)),
  updateExamUserRetake: (id, data) => apiService.put(API_ENDPOINTS.UPDATE_EXAM_USER_RETAKE(id), data),
  updateExamUserRetakeIsRetake: (id, data) => apiService.patch(API_ENDPOINTS.UPDATE_EXAM_USER_RETAKE_IS_RETAKE(id), data),
  deleteExamUserRetake: (id) => apiService.delete(API_ENDPOINTS.DELETE_EXAM_USER_RETAKE(id)),
  deleteExamUserRetakesByExamId: (examId) => apiService.delete(API_ENDPOINTS.DELETE_EXAM_USER_RETAKES_BY_EXAM_ID(examId)),
  deleteExamUserRetakesByUserId: (userId) => apiService.delete(API_ENDPOINTS.DELETE_EXAM_USER_RETAKES_BY_USER_ID(userId)),
  // Cache management methods
  clearExamUserRetakeCache: () => apiService.clearCachePattern('exam-user-retakes'),
};

// File Management API (S3 via backend)
export const fileAPI = {
  upload: async (file, folder = 'thumbnails', onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    // Strip trailing slash to avoid double-slash in S3 key (e.g. test/ -> test)
    const normalizedFolder = folder.replace(/\/+$/, '');
    formData.append('folder', normalizedFolder);

    const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken') || localStorage.getItem('admin_token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      if (onProgress && typeof onProgress === 'function') {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentage = Math.round((event.loaded / event.total) * 100);
            onProgress(percentage, event.loaded, event.total);
          }
        });
      }
      
      xhr.addEventListener('load', () => {
        
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300 && data.success) {
            // Clear file list cache so the next list call fetches fresh data
            apiService.clearCachePattern('files');
            resolve(data);
          } else {
            reject(new Error(data.message || `Upload failed with status ${xhr.status}`));
          }
        } catch {
          reject(new Error('Failed to parse upload response'));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

      xhr.open('POST', `${API_BASE_URL}${API_ENDPOINTS.FILE_UPLOAD}`);
      Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
      xhr.send(formData);
    });
  },
  list: (path = '') => {
    const query = path ? `?path=${encodeURIComponent(path)}` : '';
    return apiService.get(`${API_ENDPOINTS.FILE_LIST}${query}`);
  },
  listRecursive: (path) => {
    return apiService.get(`${API_ENDPOINTS.FILE_LIST_RECURSIVE}?path=${encodeURIComponent(path)}`);
  },
  getDownloadUrl: (key) => {
    return `${API_BASE_URL}${API_ENDPOINTS.FILE_DOWNLOAD}?key=${encodeURIComponent(key)}`;
  },
  download: (key) => {
    const url = `${API_BASE_URL}${API_ENDPOINTS.FILE_DOWNLOAD}?key=${encodeURIComponent(key)}`;
    const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken') || localStorage.getItem('admin_token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return fetch(url, { headers });
  },
  downloadFolder: (path) => {
    const url = `${API_BASE_URL}${API_ENDPOINTS.FILE_DOWNLOAD_FOLDER}?path=${encodeURIComponent(path)}`;
    const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken') || localStorage.getItem('admin_token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return fetch(url, { headers });
  },
  deleteFile: async (fullKey) => {
    const response = await apiService.delete(`${API_ENDPOINTS.FILE_DELETE}?key=${encodeURIComponent(fullKey)}`);
    apiService.clearCachePattern('files');
    return response;
  },
  deleteFolder: async (path) => {
    const response = await apiService.delete(`${API_ENDPOINTS.FILE_DELETE_FOLDER}?path=${encodeURIComponent(path)}`);
    apiService.clearCachePattern('files');
    return response;
  },
};

// Export cache management utilities
export const cacheUtils = {
  clearAllCache: () => apiService.clearCache(),
  clearUserCache: () => apiService.clearCachePattern('users'),
  clearCourseCache: () => apiService.clearCachePattern('courses'),
  clearLessonCache: () => apiService.clearCachePattern('lessons'),
  clearLessonContentCache: () => apiService.clearCachePattern('lesson-content'),
  clearQuizCache: () => apiService.clearCachePattern('quizzes'),
  clearQuestionCache: () => apiService.clearCachePattern('questions'),
  clearAnswerCache: () => apiService.clearCachePattern('answers'),
  clearCategoryCache: () => apiService.clearCachePattern('categories'),
  clearArticleCache: () => apiService.clearCachePattern('articles'),
  clearTopicCache: () => apiService.clearCachePattern('topics'),
  clearTopicLessonCache: () => apiService.clearCachePattern('topic-lessons'),
  clearExamCache: () => apiService.clearCachePattern('exams'),
  clearExamQuestionCache: () => apiService.clearCachePattern('exam-questions'),
  clearExamResultCache: () => apiService.clearCachePattern('exam-results'),
  clearExamAvailabilityCache: () => apiService.clearCachePattern('exam-availability'),
  clearClassCache: () => apiService.clearCachePattern('classes'),
  clearClassStudentCache: () => apiService.clearCachePattern('class-students'),
  clearAttendanceCache: () => apiService.clearCachePattern('attendance'),
  clearLOSCache: () => apiService.clearCachePattern('los'),
  clearExamTypeCache: () => apiService.clearCachePattern('exam-types'),
  clearClassFeedbackCache: () => apiService.clearCachePattern('class-feedback'),
  clearFeedbackQuestionCache: () => apiService.clearCachePattern('feedback-questions'),
  clearFeedbackResponseCache: () => apiService.clearCachePattern('feedback-responses'),
  clearFeedbackDataCache: () => apiService.clearCachePattern('feedback-data'),
  clearUserAnswerCache: () => apiService.clearCachePattern('user-answers'),
  clearUserServiceCache: () => apiService.clearCachePattern('user-services'),
  clearClassSharedFileCache: () => apiService.clearCachePattern('class-shared-files'),
  clearClassAssignmentCache: () => apiService.clearCachePattern('class-assignments'),
  clearAssignmentSubmissionCache: () => apiService.clearCachePattern('assignment-submissions'),
  clearAdminCache: () => apiService.clearCachePattern('admin'),
  clearWebDataCache: () => apiService.clearCachePattern('webdata'),
  clearExamUserRetakeCache: () => apiService.clearCachePattern('exam-user-retakes'),
};
export default apiService;
